import {
  DatePicker,
  LocalizationProvider,
  DateTimePicker as TimeDatePicker,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useAppSelector } from "src/redux/hooks";
import CustomSvgIcon from "../Icons/CustomSvgIcon";
import CalendarSvg from "src/assets/formElements/Calendar.svg";

interface DateTimePickerProps {
  fieldName: string;
  label: string;
  fullWidth?: boolean;
  control: any;
  format?: string;
  error?: boolean;
  helperText?: any;
  isDate?: boolean;
  defaultValue?: string | null | moment.Moment;
  disabled?: boolean;
  InputProps?: object;
  style?: object;
  onChangeFunction?: any;
  minDate?: any;
  // optional: provide yup schema so component can show required asterisk on mount
  yupSchema?: any;
  required?: boolean;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  fieldName,
  label,
  fullWidth,
  control,
  format,
  error,
  helperText,
  isDate = true,
  defaultValue,
  InputProps = {},
  style,
  disabled = false,
  onChangeFunction,
  minDate,
  yupSchema,
  required = false,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollPositionRef = React.useRef<{
    windowX: number;
    windowY: number;
    parentEl: HTMLElement | null;
    parentTop: number;
    parentLeft: number;
  } | null>(null);

  const handleOpen = () => {
    // Find the closest scrollable parent
    let parentEl: HTMLElement | null = null;
    let current: HTMLElement | null = containerRef.current;
    while (current) {
      if (current === document.body || current === document.documentElement) {
        break;
      }
      if (current.scrollHeight > current.clientHeight) {
        const overflowY = window.getComputedStyle(current).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") {
          parentEl = current;
          break;
        }
      }
      current = current.parentElement;
    }

    scrollPositionRef.current = {
      windowX: window.scrollX || window.pageXOffset,
      windowY: window.scrollY || window.pageYOffset,
      parentEl,
      parentTop: parentEl ? parentEl.scrollTop : 0,
      parentLeft: parentEl ? parentEl.scrollLeft : 0,
    };

    setTimeout(() => {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 50);
  };

  const handleClose = () => {
    if (scrollPositionRef.current) {
      const { windowX, windowY, parentEl, parentTop, parentLeft } =
        scrollPositionRef.current;

      if (parentEl) {
        parentEl.scrollTo({
          top: parentTop,
          left: parentLeft,
          behavior: "smooth",
        });
      }

      window.scrollTo({
        top: windowY,
        left: windowX,
        behavior: "smooth",
      });

      scrollPositionRef.current = null;
    }
  };
  const { dateTimeFormat, dateOnlyFormat } = useAppSelector(
    (state) => state.clientSettings
  );
  const dateFormat =
    format || isDate
      ? dateOnlyFormat || "MM/DD/YYYY"
      : dateTimeFormat || "MM/DD/YYYY HH:mm";

  const methods = useFormContext();
  const computeIsRequired = React.useCallback(() => {
    if (required) return true;
    try {
      if (yupSchema) {
        if (typeof yupSchema.describe === "function") {
          const desc = yupSchema.describe();
          const f = desc?.fields?.[fieldName];
          if (f?.tests && f.tests.some((t: any) => t?.name === "required")) {
            return true;
          }
        }
        const fieldSchema = yupSchema.fields?.[fieldName] ?? yupSchema?.[fieldName];
        if (fieldSchema) {
          const tests = fieldSchema.tests ?? fieldSchema._tests ?? [];
          if (Array.isArray(tests) && tests.some((t: any) => t?.name === "required" || t?.TEST === "required")) {
            return true;
          }
        }
      }
    } catch {
      /* ignore */
    }

    try {
      const fieldState = methods?.getFieldState ? methods.getFieldState(fieldName) : null;
      return !!fieldState?.invalid;
    } catch {
      return false;
    }
  }, [required, yupSchema, fieldName, methods]);

  const [isRequiredState, setIsRequiredState] = React.useState<boolean>(() => computeIsRequired());
  React.useEffect(() => {
    setIsRequiredState(computeIsRequired());
  }, [computeIsRequired]);

  const labelWithAsterisk = !label ? "" : isRequiredState ? `${label} *` : label;

  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: error ? "0px" : "2px",
      borderColor: error ? "transparent" : "primary.main",
      borderRadius: "8px",
    },
    height: 38,
    backgroundColor: "rgba(63, 141, 124, 0.00)",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: error ? "transparent" : "primary.main",
    },

    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.85)",
    },
    ...style,
  };

  const customIcon = () => <CustomSvgIcon src={CalendarSvg} />;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Controller
        name={fieldName || ""}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) =>
          isDate ? (
            <DatePicker
              {...field}
              disabled={disabled}
              label={labelWithAsterisk || ""}
              value={field.value ? moment(field.value) : null}
              onOpen={handleOpen}
              onClose={handleClose}
              format={dateFormat}
              slots={{ openPickerIcon: customIcon }}
              minDate={minDate}
              onChange={(date: any) => {
                if (date === null) {
                  field.onChange(null);
                  if (onChangeFunction) {
                    onChangeFunction(null, field);
                  }
                } else if (onChangeFunction) {
                  const dateValue = onChangeFunction(date, field);
                  field.onChange(
                    moment(dateValue, dateFormat).format(
                      isDate ? "YYYY-MM-DD" : "YYYY-MM-DDTHH:mm:ss"
                    )
                  );
                } else {
                  field.onChange(
                    moment(date, dateFormat).format(
                      isDate ? "YYYY-MM-DD" : "YYYY-MM-DDTHH:mm:ss"
                    )
                  );
                }
              }}
              slotProps={{
                popper: {
                  // sx: {
                  //   zIndex: (theme) => theme.zIndex.modal + 1,
                  // },
                  modifiers: [
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: ["bottom", "top", "right", "left"],
                      },
                    },
                  ],
                },
                textField: {
                  ref: containerRef,
                  fullWidth: fullWidth || true,
                  error: error,
                  helperText: helperText || "",
                  variant: "outlined",
                  size: "small",
                  InputProps: {
                    sx: commonTextFieldStyles,
                  },
                  InputLabelProps: {
                    sx: {
                      color: "secondary.main",
                      background: "none",
                      padding: "0 5px",
                    },
                  },
                },
              }}
              {...InputProps}
            />
          ) : (
            <TimeDatePicker
              {...field}
              disabled={disabled}
              minDate={minDate}
              label={labelWithAsterisk || ""}
              value={field.value ? moment(field.value) : null}
              onOpen={handleOpen}
              onClose={handleClose}
              format={dateFormat}
              slots={{ openPickerIcon: customIcon }}
              onChange={(date: any) => {
                if (date === null) {
                  field.onChange(null);
                  if (onChangeFunction) {
                    onChangeFunction(null, field);
                  }
                } else if (onChangeFunction) {
                  const dateValue = onChangeFunction(date, field);
                  field.onChange(
                    moment(dateValue, dateFormat).format(
                      isDate ? "YYYY-MM-DD" : "YYYY-MM-DDTHH:mm:ss"
                    )
                  );
                } else {
                  field.onChange(
                    moment(date, dateFormat).format(
                      isDate ? "YYYY-MM-DD" : "YYYY-MM-DDTHH:mm:ss"
                    )
                  );
                }
              }}
              slotProps={{
                popper: {
                  // sx: {
                  //   zIndex: (theme) => theme.zIndex.modal + 1,
                  // },
                  modifiers: [
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: ["bottom", "top", "right", "left"],
                      },
                    },
                  ],
                },
                textField: {
                  ref: containerRef,
                  fullWidth: fullWidth || true,
                  error: error,
                  helperText: helperText || "",
                  variant: "outlined",
                  size: "small",
                  InputProps: {
                    sx: commonTextFieldStyles,
                    ...InputProps,
                  },
                  InputLabelProps: {
                    sx: {
                      color: "secondary.main",
                      background: "none",
                      padding: "0 5px",
                    },
                  },
                },
              }}
              {...InputProps}
            />
          )
        }
      />
    </LocalizationProvider>
  );
};

export default DateTimePicker;
